import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import {
  Phone,
  MapPin,
  Facebook,
  Instagram,
  Twitter,
  MessageCircle,
  Shield,
} from "lucide-react";
import { useIsStandalone } from "@/hooks";
import { useGetAppConfig } from "@/hooks/data";
import type { EntityModel } from "@/types";
import { useTheme } from "@/hooks/useTheme";
import { trackClickToCall } from "@/utils/analytics";

interface FooterProps {
  data?: EntityModel;
}

const NAV_LINKS = [
  { label: "KEY.HOME", to: "/" },
  { label: "KEY.MENU", to: "/full-menu" },
  { label: "KEY.ABOUT", to: "/about" },
  { label: "KEY.CONTACT", to: "/contact" },
];

const WORKING_HOURS = [
  { dayKey: "FOOTER.MONDAY", hours: "11:00 – 22:00" },
  { dayKey: "FOOTER.TUESDAY", hours: "11:00 – 22:00" },
  { dayKey: "FOOTER.WEDNESDAY", hours: "11:00 – 22:00" },
  { dayKey: "FOOTER.THURSDAY", hours: "11:00 – 22:00" },
  { dayKey: "FOOTER.FRIDAY", hours: "11:00 – 23:00" },
  { dayKey: "FOOTER.SATURDAY", hours: "11:00 – 23:00" },
  { dayKey: "FOOTER.SUNDAY", hours: "13:00 – 22:00" },
];

const SectionTitle = ({ children }: { children: React.ReactNode }) => (
  <h4 className="text-sm font-extrabold text-primary uppercase tracking-[0.18em] pb-2 border-b border-primary/20 mb-4">
    {children}
  </h4>
);

export const Footer = ({ data }: FooterProps) => {
  const { t, i18n } = useTranslation();
  const locale = i18n.language;
  const isStandalone = useIsStandalone();
  const { isDark } = useTheme();

  const format12Hour = (timeStr?: string, locale: string = "en") => {
    if (!timeStr) return "";
    const [h, m] = timeStr.split(":");
    let hour = parseInt(h, 10);
    const isPM = hour >= 12;
    hour = hour % 12 || 12;

    if (locale === "ar") {
      return `${hour}:${m} ${isPM ? "م" : "ص"}`;
    } else if (locale === "ku") {
      return `${hour}:${m} ${isPM ? "د.ن" : "پ.ن"}`;
    }
    return `${hour}:${m} ${isPM ? "PM" : "AM"}`;
  };

  const { data: appConfigData } = useGetAppConfig();
  const branch = appConfigData?.data?.branch;

  const primaryContact = branch?.contacts?.find(
    (c: any) => c.is_primary && c.contact_type === "phone",
  );
  const primaryLocation = branch?.locations?.find((l: any) => l.is_primary);
  const primaryEmail = branch?.contacts?.find(
    (c: any) => c.is_primary && c.contact_type === "email",
  );

  const address = primaryLocation
    ? `${primaryLocation.address_line_1}, ${primaryLocation.city}, ${primaryLocation.country}`
    : locale === "en"
      ? data?.address_en
      : data?.address_ar;

  const phone =
    primaryContact?.contact_value || data?.phone || "+964 750 000 0000";
  const email =
    primaryEmail?.contact_value || data?.email || "sewargourmet@gmail.com";

  const getDayKey = (day: string) => {
    const days: any = {
      saturday: "FOOTER.SATURDAY",
      sunday: "FOOTER.SUNDAY",
      monday: "FOOTER.MONDAY",
      tuesday: "FOOTER.TUESDAY",
      wednesday: "FOOTER.WEDNESDAY",
      thursday: "FOOTER.THURSDAY",
      friday: "FOOTER.FRIDAY",
    };
    return days[day?.toLowerCase()] || day;
  };

  const displayHours = appConfigData?.data?.working_hours?.length
    ? appConfigData.data.working_hours.map((hour: any) => ({
        dayKey: getDayKey(hour.day_of_week),
        isClosed: hour.is_closed,
        open: format12Hour(hour.open_time, locale),
        close: format12Hour(hour.close_time, locale),
        hours: t("KEY.CLOSED", "Closed"),
      }))
    : WORKING_HOURS.map((h) => ({
        dayKey: h.dayKey,
        isClosed: false,
        open: h.hours.split(" – ")[0],
        close: h.hours.split(" – ")[1],
        hours: h.hours,
      }));

  const getSocialIcon = (platform: string) => {
    switch (platform) {
      case "facebook":
        return <Facebook className="size-[18px]" />;
      case "instagram":
        return <Instagram className="size-[18px]" />;
      case "twitter":
        return <Twitter className="size-[18px]" />;
      default:
        return <MessageCircle className="size-[18px]" />;
    }
  };

  const socialLinks = branch?.social_media ?? [
    { id: "ig", platform: "instagram", url: "https://instagram.com" },
    { id: "wa", platform: "whatsapp", url: "https://wa.me/1234567890" },
    { id: "fb", platform: "facebook", url: "https://facebook.com" },
    { id: "tw", platform: "twitter", url: "https://twitter.com" },
  ];

  return (
    <footer className="bg-white dark:bg-cardBg text-primary dark:text-primaryText font-['PlutoRegular'] pb-20 md:pb-0 transition-colors duration-300">
      {/* ── Decorative top border ── */}
      <div className="h-[3px] bg-gradient-to-r from-transparent via-primary/40 to-transparent" />

      {/* ── Main grid ── */}
      <div className="custom-container py-12 md:py-16">
        {/* ─── MOBILE & TABLET LAYOUT (below lg) ─── */}
        <div className="lg:hidden space-y-10">
          {/* Brand - Centered */}
          <div className="flex flex-col items-center text-center space-y-4">
            <Link to="/" className="inline-block">
              <img
                src={isDark ? "/images/logo-new.png" : "/images/logo.png"}
                alt={t("FOOTER.BRAND_NAME")}
                loading="lazy"
                decoding="async"
                className="h-20 w-auto object-contain"
              />
            </Link>
            <p className="text-sm font-semibold text-primary/70 leading-relaxed max-w-xs">
              {t("FOOTER.TAGLINE")}
            </p>
            {/* Social icons - centered under brand */}
            <div className="flex flex-wrap gap-2.5 justify-center pt-2">
              {socialLinks.map((social: any) => (
                <a
                  key={social.id}
                  href={social.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={social.platform}
                  className="size-10 rounded-xl bg-primary/10 hover:bg-primary text-primary hover:text-[#EAE4DB] flex items-center justify-center transition-all duration-200 shadow-sm"
                >
                  {getSocialIcon(social.platform)}
                </a>
              ))}
            </div>
          </div>

          {/* Two-column grid for Quick Links + Contact */}
          <div className="grid grid-cols-2 gap-8">
            {/* Quick Links */}
            {!isStandalone && (
              <div>
                <SectionTitle>{t("FOOTER.QUICK_LINKS")}</SectionTitle>
                <ul className="space-y-2.5">
                  {NAV_LINKS.map((link) => (
                    <li key={link.to}>
                      <Link
                        to={link.to}
                        className="text-sm font-semibold text-primary/80 hover:text-primary transition-colors duration-200 flex items-center gap-1.5 group"
                      >
                        <span className="w-1 h-1 rounded-full bg-primary/40 group-hover:bg-primary transition-colors duration-200 shrink-0" />
                        {t(link.label)}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Contact Info */}
            <div>
              <SectionTitle>{t("FOOTER.CONTACT_INFO")}</SectionTitle>
              <ul className="space-y-3">
                <li className="flex items-start gap-2 text-sm">
                  <span className="size-7 rounded-lg bg-primary/10 flex items-center justify-center shrink-0 mt-0.5">
                    <MapPin className="size-4 text-primary" />
                  </span>
                  <span className="font-semibold text-primary/80 leading-snug text-xs">
                    {address || t("FOOTER.ADDRESS")}
                  </span>
                </li>
                <li className="flex items-center gap-2 text-sm">
                  <span className="size-7 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
                    <Phone className="size-4 text-primary" />
                  </span>
                  <a
                    href={`tel:${String(phone).replace(/\s+/g, "")}`}
                    onClick={() => trackClickToCall("footer")}
                    className="font-bold text-primary/85 tabular-nums text-xs hover:underline"
                    dir="ltr"
                  >
                    {phone}
                  </a>
                </li>
              </ul>
            </div>
          </div>

          {/* Working Hours - Full width card */}
          <div className="bg-white/60 dark:bg-surfaceAlt/60 rounded-2xl p-5 border border-primary/8 dark:border-border-2 transition-colors">
            <SectionTitle>{t("FOOTER.WORKING_HOURS")}</SectionTitle>
            <ul className="space-y-2">
              {displayHours.map((hour: any) => (
                <li
                  key={hour.dayKey}
                  className="flex justify-between gap-3 text-sm"
                >
                  <span className="font-bold text-primary/85">
                    {t(hour.dayKey)}
                  </span>
                  <span
                    className="font-semibold text-primary/65 tabular-nums flex items-center gap-1.5"
                  >
                    {hour.isClosed ? (
                      <span>{hour.hours}</span>
                    ) : (
                      <>
                        <span>{hour.open}</span>
                        <span>-</span>
                        <span>{hour.close}</span>
                      </>
                    )}
                  </span>
                </li>
              ))}
            </ul>
          </div>

          {/* Map - Full width */}
          <div>
            <SectionTitle>{t("FOOTER.LOCATION")}</SectionTitle>
            <div className="rounded-2xl overflow-hidden shadow-md border border-primary/10">
              <iframe
                title="Google Maps Location"
                src={
                  primaryLocation
                    ? `https://maps.google.com/maps?q=${encodeURIComponent(
                        `${primaryLocation.address_line_1} ${primaryLocation.city} ${primaryLocation.country}`,
                      )}&t=&z=15&ie=UTF8&iwloc=&output=embed`
                    : `https://maps.google.com/maps?q=${encodeURIComponent(address || "Erbil, Iraq")}&t=&z=15&ie=UTF8&iwloc=&output=embed`
                }
                width="100%"
                height="200"
                style={{ border: 0 }}
                allowFullScreen
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                className="w-full block"
              />
            </div>
            <a
              href={
                primaryLocation?.google_maps_url ||
                `https://maps.google.com/?q=${encodeURIComponent(address || "Erbil Iraq")}`
              }
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 mt-3 text-sm font-bold text-primary hover:text-elegantBlack transition-colors duration-200 underline underline-offset-2"
            >
              {t("FOOTER.OPEN_MAP")}
            </a>
          </div>
        </div>

        {/* ─── DESKTOP LAYOUT (lg and above) — UNCHANGED ─── */}
        <div className="hidden lg:block">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-10 lg:gap-8 items-start">
            {/* ① Brand */}
            <div className="space-y-5 lg:col-span-1">
              <Link to="/" className="inline-block">
                <img
                  src={isDark ? "/images/logo-new.png" : "/images/logo.png"}
                  alt={t("FOOTER.BRAND_NAME")}
                  loading="lazy"
                  decoding="async"
                  className="h-16 w-auto object-contain"
                />
              </Link>
              <p className="text-sm font-semibold text-primary/70 leading-relaxed">
                {t("FOOTER.TAGLINE")}
              </p>
            </div>

            {/* ② Quick Links */}
            {!isStandalone && (
              <div className="lg:col-span-1">
                <SectionTitle>{t("FOOTER.QUICK_LINKS")}</SectionTitle>
                <ul className="space-y-2.5">
                  {NAV_LINKS.map((link) => (
                    <li key={link.to}>
                      <Link
                        to={link.to}
                        className="text-sm font-semibold text-primary/80 hover:text-primary transition-colors duration-200 flex items-center gap-1.5 group"
                      >
                        <span className="w-1 h-1 rounded-full bg-primary/40 group-hover:bg-primary transition-colors duration-200 shrink-0" />
                        {t(link.label)}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* ③ Working Hours */}
            <div className="lg:col-span-1">
              <SectionTitle>{t("FOOTER.WORKING_HOURS")}</SectionTitle>
              <ul className="space-y-2">
                {displayHours.map((hour: any) => (
                  <li
                    key={hour.dayKey}
                    className="flex justify-between gap-3 text-sm"
                  >
                    <span className="font-bold text-primary/85">
                      {t(hour.dayKey)}
                    </span>
                    <span
                      className="font-semibold text-primary/65 tabular-nums flex items-center gap-1.5"
                    >
                      {hour.isClosed ? (
                        <span>{hour.hours}</span>
                      ) : (
                        <>
                          <span>{hour.open}</span>
                          <span>-</span>
                          <span>{hour.close}</span>
                        </>
                      )}
                    </span>
                  </li>
                ))}
              </ul>
            </div>

            {/* ④ Contact + Social */}
            <div className="lg:col-span-1">
              <SectionTitle>{t("FOOTER.CONTACT_INFO")}</SectionTitle>
              <ul className="space-y-3 mb-5">
                <li className="flex items-start gap-2.5 text-sm">
                  <span className="size-7 rounded-lg bg-primary/10 flex items-center justify-center shrink-0 mt-0.5">
                    <MapPin className="size-4 text-primary" />
                  </span>
                  <span className="font-semibold text-primary/80 leading-snug">
                    {address || t("FOOTER.ADDRESS")}
                  </span>
                </li>
                <li className="flex items-center gap-2.5 text-sm">
                  <span className="size-7 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
                    <Phone className="size-4 text-primary" />
                  </span>
                  <a
                    href={`tel:${String(phone).replace(/\s+/g, "")}`}
                    onClick={() => trackClickToCall("footer")}
                    className="font-bold text-primary/85 tabular-nums hover:underline"
                    dir="ltr"
                  >
                    {phone}
                  </a>
                </li>
              </ul>
              {/* Social icons */}
              <div className="flex flex-wrap gap-2 pt-1">
                {socialLinks.map((social: any) => (
                  <a
                    key={social.id}
                    href={social.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={social.platform}
                    className="size-9 rounded-xl bg-primary/10 hover:bg-primary text-primary hover:text-[#EAE4DB] flex items-center justify-center transition-all duration-200 shadow-sm"
                  >
                    {getSocialIcon(social.platform)}
                  </a>
                ))}
              </div>
            </div>

            {/* ⑤ Map */}
            <div className="lg:col-span-1">
              <SectionTitle>{t("FOOTER.LOCATION")}</SectionTitle>
              <div className="rounded-2xl overflow-hidden shadow-md border border-primary/10">
                <iframe
                  title="Google Maps Location"
                  src={
                    primaryLocation
                      ? `https://maps.google.com/maps?q=${encodeURIComponent(
                          `${primaryLocation.address_line_1} ${primaryLocation.city} ${primaryLocation.country}`,
                        )}&t=&z=15&ie=UTF8&iwloc=&output=embed`
                      : `https://maps.google.com/maps?q=${encodeURIComponent(address || "Erbil, Iraq")}&t=&z=15&ie=UTF8&iwloc=&output=embed`
                  }
                  width="100%"
                  height="190"
                  style={{ border: 0 }}
                  allowFullScreen
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  className="w-full block"
                />
              </div>
              <a
                href={
                  primaryLocation?.google_maps_url ||
                  `https://maps.google.com/?q=${encodeURIComponent(address || "Erbil Iraq")}`
                }
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 mt-3 text-sm font-bold text-primary hover:text-elegantBlack transition-colors duration-200 underline underline-offset-2"
              >
                {t("FOOTER.OPEN_MAP")}
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* ── Bottom bar ── */}
      <div className="border-t border-border-2 bg-white dark:bg-mainBg transition-colors">
        <div className="custom-container py-5 flex flex-col lg:flex-row justify-between items-center gap-4 text-xs font-semibold text-primary/70 text-center lg:text-start">
          <p className="leading-relaxed">
            &copy; {new Date().getFullYear()}{" "}
            <span className="text-primary/70">
              {branch?.name || t("FOOTER.BRAND_NAME")}
            </span>{" "}
            — {t("FOOTER.RIGHTS")}
          </p>
          <div className="flex flex-col lg:flex-row items-center gap-2.5 lg:gap-3">
            <Link
              to="/privacy-policy"
              className="flex items-center gap-1.5 text-primary/60 hover:text-primary transition-colors duration-200 underline-offset-2 hover:underline"
            >
              <Shield className="w-3 h-3" />
              {t("PRIVACY.LINK_TEXT")}
            </Link>
            <span className="hidden lg:block text-primary/30">·</span>
            <div className="flex flex-col lg:flex-row items-center gap-2.5 lg:gap-3 text-center" dir="ltr">
              <span className="leading-relaxed max-w-[280px] lg:max-w-none">{address || "Erbil, Iraq"}</span>
              <span className="hidden lg:block text-primary/30">·</span>
              <span>{phone}</span>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};
